import React, { type FC } from "react";
import { useDispatch } from "react-redux";

import { Button } from "~/components/button";
import { IconCheck, IconExternalLink, IconLogoStackState } from "~/components/icon";
import { Mode } from "~/models/dto";

import { playgroundModal } from "../../app/state/pages/WelcomeSlice";
import { usageTrack } from "../../app/state/usage-track/UsageTrackActions";
import { useDocLink } from "../../components/react/docLink/use-doc-link";
import { Modal } from "../../components/react/modal/Modal";

export interface PlaygroundPopupProps {
	open: boolean;
}

export const PlaygroundPopup: FC<PlaygroundPopupProps> = ({ open: isOpen }) => {
	const dispatch = useDispatch();
	const getStartedDocLink = useDocLink("ui-get-started-popup-link");

	return (
		<Modal
			bodyStyle={{
				padding: 0,
			}}
			onClose={() => {
				dispatch(playgroundModal({ open: false }));
			}}
			open={isOpen}
			size={810}
		>
			<div className="relative flex h-[450px] overflow-hidden" data-testid="playground-popup">
				<div className="min-w-[350px] bg-graphite-1100 px-12 pt-[50px] font-sans text-white">
					<div className="wrap-break-word font-medium text-2xl leading-[34px]">
						<IconLogoStackState className="pointer-events-none mb-8 h-8 w-8 text-white" />
						<p className="m-0 mb-8">
							Our playground is <span className="text-graphite-500">a nice starting point, but it’s quite</span>{" "}
							limited.
						</p>
						<p className="m-0">
							<span className="text-graphite-500">Unlock all the features and</span> experience the true power of
							observability.
						</p>
					</div>
				</div>
				<div className="min-w-[513px] pt-12 pl-12">
					<h2 className="m-0 pb-2 font-bold text-[28px] text-graphite-1000 leading-[33px]">
						Sign up for our free trial
					</h2>
					<p className="m-0 pb-8 font-light text-graphite-500 text-sm leading-4">
						No credit card required, cancel at any time
					</p>
					<ul className="m-0 list-none p-0">
						<ListItem>
							Install <DottedText>StackPacks</DottedText> and connect your own data
						</ListItem>
						<ListItem>
							Manage <DottedText>views</DottedText>
						</ListItem>
						<ListItem>
							Manage <DottedText>event handlers</DottedText>
						</ListItem>
						<ListItem>
							Manage <DottedText>health checks</DottedText>
						</ListItem>
						<ListItem>And more...</ListItem>
					</ul>
					<Button
						className="mt-8 gap-2 px-6 uppercase"
						data-trackid="hp-popup-tryfree-button"
						onClick={() => {
							dispatch(usageTrack({ name: "NUMBER_OF_CTA_CLICKS", value: 1, mode: Mode.Increment }));
							dispatch(usageTrack({ name: "PLAYGROUND_TRY_FREE_BUTTON", value: "modal", mode: Mode.Set }));
							open(getStartedDocLink, "_blank");
						}}
						variant="primary"
					>
						Try free <IconExternalLink />
					</Button>
				</div>
			</div>
		</Modal>
	);
};

const ListItem: FC<React.PropsWithChildren> = ({ children }) => (
	<li className="flex pb-4 text-base">
		<IconCheck className="mr-4 h-4 w-4 shrink-0 text-primary-600" />
		<p className="mb-0 font-light text-graphite-1000 leading-[19px]">{children}</p>
	</li>
);

const DottedText: FC<React.ComponentProps<"span">> = (props) => (
	<span {...props} className="border-0 border-graphite-1000 border-b border-dotted" />
);
